// OverviewPage.tsx — Section I: Overview.
//
// "A single glance at the table. The metrics that matter when you have ten seconds."
// (Lore-approved OV-03)
//
// Wired to dominus_admin.admin_get_aggregated_metrics() via useAdminMetrics.
// Auto-refreshes every 30s. Falls back to placeholder values while loading.
//
// Card layout: 4-column grid on desktop, 2-column on tablet, 1-column on mobile.
// All 8 cards present: Active Subscriptions, MRR, Games In Progress, Cycles Runway,
// Total Profiles, Site Visits Today, Conversion Rate, Churn / 30d.
//
// Copy is EXACTLY the Lore-approved Final column from the copy table.

import React from 'react';
import type { Identity } from '@dfinity/agent';
import MetricCard from '../components/MetricCard';
import { useAdminMetrics } from '../hooks/useAdminMetrics';

interface OverviewPageProps {
  identity: Identity | null;
}

// Format cents to dollars string, e.g. 123253 -> "$1,232.53"
function formatCents(cents: bigint): { dollars: string; sub: string } {
  const total = Number(cents);
  const dollars = Math.floor(total / 100);
  const subCents = total % 100;
  const formatted = '$' + dollars.toLocaleString('en-US');
  const sub = '.' + String(subCents).padStart(2, '0');
  return { dollars: formatted, sub };
}

// Format bigint to locale string with no sub
function formatNat(n: bigint): string {
  return Number(n).toLocaleString('en-US');
}

export default function OverviewPage({ identity }: OverviewPageProps): React.ReactElement {
  const { metrics, isLoading, lastFetchedAt, refresh } = useAdminMetrics(identity);

  const sub = metrics?.subscription;
  const auth = metrics?.auth;
  const game = metrics?.game;

  // Derive display values from canister data, with safe fallbacks.
  const activeSubs = sub ? formatNat(sub[0]?.activeSubscriptions ?? 0n) : '—';
  // Trials: '—' when the subscription rollup is absent (no data fetched yet),
  // a real count (incl. 0) when present — mirrors the activeSubs pattern so a
  // future wiring gap reads as "no data", not a misleading hard zero.
  const activeTrials = sub ? formatNat(sub[0]?.activeTrials ?? 0n) : '—';
  const mrr = sub && sub[0] ? formatCents(sub[0].mrrCents) : { dollars: '—', sub: '' };
  const gamesInProgress = game ? formatNat(game[0]?.gamesInProgress ?? 0n) : '—';
  const totalProfiles = auth ? formatNat(auth[0]?.totalProfiles ?? 0n) : '—';
  const profilesNew24h = auth && auth[0] ? String(auth[0].profilesCreatedLast24h) : '0';

  // Churn and conversion are mocked until Cloudflare Analytics is wired (Phase B).
  // These come from subscription + Cloudflare respectively.
  const churnPct = sub && sub[0] ? (Number(sub[0].churnLast30d) / Math.max(Number(sub[0].activeSubscriptions), 1) * 100).toFixed(1) : '—';

  return (
    <section className="section" id="overview" aria-labelledby="overview-title">
      <header className="section-header">
        <span className="section-eyebrow">Section I</span>
        <h1 className="section-title" id="overview-title">Overview</h1>
        <p className="section-lede">
          A single glance at the table. The metrics that matter when you have ten seconds.
        </p>
        {lastFetchedAt && (
          <p style={{
            fontFamily: 'var(--font-body)',
            fontStyle: 'italic',
            fontSize: '13px',
            color: 'var(--ivory-d)',
            marginTop: 'var(--s-xs)',
          }}>
            Last refreshed · {lastFetchedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            {' '}&nbsp;
            <button
              onClick={refresh}
              disabled={isLoading}
              style={{
                fontFamily: 'var(--font-ui)',
                fontStyle: 'normal',
                fontSize: '10px',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: 'var(--gold-d)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
              }}
            >
              {isLoading ? 'Refreshing' : 'Refresh'}
            </button>
          </p>
        )}
      </header>

      <div className="grid grid-4">
        {/* OV-04 / OV-05 */}
        <MetricCard
          label="Active Subscriptions"
          numeral={activeSubs}
          caption="Members in good standing. Renewing on cycle."
          trend={sub && sub[0] ? `+${sub[0].signupsLast7d} / 7d` : undefined}
          trendDir="up"
        />

        {/* Users on Trials — wired to subscription_canister activeTrials */}
        <MetricCard
          label="Users on Trials"
          numeral={activeTrials}
          caption="Card on file. Yet to be charged."
          trend={sub && sub[0] ? `+${sub[0].trialsStartedLast7d} / 7d` : undefined}
          trendDir="up"
        />

        {/* OV-06 / OV-07 */}
        <MetricCard
          label="Monthly Recurring Revenue"
          numeral={mrr.dollars}
          numeralSub={mrr.sub}
          caption="Net of refunds. Gross before Stripe fees."
          trend="+4.9% / 30d"
          trendDir="up"
        />

        {/* OV-08 / OV-09 */}
        <MetricCard
          label="Games In Progress"
          numeral={gamesInProgress}
          caption="Tables with at least one human seated."
          trend="Live"
          trendDir="flat"
        />

        {/* OV-10 / OV-11 — Cycles Runway placeholder until Operations health wired */}
        <MetricCard
          label="Cycles Runway"
          numeral="—"
          numeralSub="days"
          caption="At current burn. Trigger: under 1T cycles. Top-up: 2T. Daily cap: 10T."
          trend="pooled"
          trendDir="flat"
        />

        {/* OV-12 / OV-13 */}
        <MetricCard
          label="Total Profiles"
          numeral={totalProfiles}
          caption="Lifetime accounts created."
          trend={`+${profilesNew24h} / 24h`}
          trendDir="up"
        />

        {/* OV-14 / OV-15 — Cloudflare Analytics wired in Phase B */}
        <MetricCard
          label="Site Visits Today"
          numeral="—"
          caption="Pageviews on playdominus.com. Cloudflare Analytics."
          trend="—"
          trendDir="flat"
        />

        {/* OV-16 / OV-17 — Conversion Rate from Cloudflare + Stripe (Phase B) */}
        <MetricCard
          label="Conversion Rate"
          numeral="—"
          numeralSub="%"
          caption="Visit to subscription. Past seven days."
          trend="7d trailing"
          trendDir="flat"
        />

        {/* OV-18 / OV-19 */}
        <MetricCard
          label="Churn / 30d"
          numeral={churnPct === '—' ? '—' : churnPct}
          numeralSub="%"
          caption="Cancellations and lapses."
          trend={churnPct !== '—' && Number(churnPct) > 5 ? 'elevated' : undefined}
          trendDir={churnPct !== '—' && Number(churnPct) > 5 ? 'down' : 'flat'}
          status={churnPct !== '—' && Number(churnPct) > 10 ? 'crit' : churnPct !== '—' && Number(churnPct) > 5 ? 'warn' : 'default'}
        />
      </div>
    </section>
  );
}
